import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { RETURN_URL_PARAM, sanitizeReturnPath, sanitizeStaffReturnPath } from '../../utils/authReturnUrl';
import {
  isSubdomainRoutingEnabled,
  isWorkspaceHost,
  toWorkspaceUrl,
  wsPath,
} from '../../utils/siteHosts';
import {
  authErrorClass,
  authLinkClass,
  authSecondaryButtonClass,
} from './authFormStyles';
import LoginForm from './LoginForm';
import MfaForm from './MfaForm';
import SignedInPanel from './SignedInPanel';
import RegisterForm from './RegisterForm';

type AuthPanelProps = {
  initialMode?: 'login' | 'register';
  onModeChange?: (mode: 'login' | 'register') => void;
  lockMode?: 'login' | 'register';
  minimal?: boolean;
};

function RegistrationSuccessBanner({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      aria-live="polite"
      className="mb-5 rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-4 sm:mb-6"
    >
      <div className="flex gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-emerald-100 text-emerald-800"
          aria-hidden
        >
          <MailCheck className="h-5 w-5" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-emerald-950">
            {t('auth.register_success_banner_title')}
          </p>
          <p className="mt-1 text-sm text-emerald-900">{message}</p>
        </div>
      </div>
    </div>
  );
}

function AuthPanel({
  initialMode = 'login',
  onModeChange,
  lockMode,
  minimal = false,
}: AuthPanelProps) {
  const { isAuthenticated, auth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTarget = searchParams.get(RETURN_URL_PARAM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mode, setMode] = useState<'login' | 'register'>(initialMode);

  const isDisabledAccountError = React.useMemo(() => {
    const msg = String(error || '').toLowerCase();
    return (
      msg.includes('account is disabled') ||
      msg.includes('disabled') ||
      msg.includes('suspended') ||
      msg.includes('blocked')
    );
  }, [error]);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (auth.type === 'CUSTOMER') {
      const rawResume = returnTarget ? sanitizeReturnPath(returnTarget) : null;
      navigate(rawResume ?? '/account', { replace: true });
      return;
    }

    const isStaffType =
      auth.type === 'ADMIN' ||
      auth.type === 'EMPLOYEE' ||
      auth.type === 'SHIPPING_AGENT';

    if (!isStaffType) return;

    const rawResume = returnTarget ? sanitizeStaffReturnPath(returnTarget) : null;
    const destination = rawResume ?? (
      auth.type === 'SHIPPING_AGENT'
        ? wsPath('/workspace/delivery/scan')
        : wsPath('/workspace')
    );
    if (isSubdomainRoutingEnabled()) {
      if (isWorkspaceHost()) {
        navigate(destination, { replace: true });
      } else {
        window.location.assign(toWorkspaceUrl(destination));
      }
    } else {
      navigate(destination, { replace: true });
    }
  }, [auth.type, isAuthenticated, navigate, returnTarget]);

  useEffect(() => {
    const modeParam = searchParams.get('mode');
    if (modeParam === 'register') {
      setMode('register');
    }
  }, [searchParams]);

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  if (isAuthenticated) {
    return <SignedInPanel />;
  }

  if (auth.mfaRequired) {
    return (
      <div className="space-y-5">
        <h2 className="text-lg font-semibold tracking-tight sm:text-xl">
          {t('auth.mfa_title')}
        </h2>
        {error && (
          <p className="text-xs text-red-400">
            {error}
          </p>
        )}
        <MfaForm onError={setError} />
      </div>
    );
  }

  const showLogin = (lockMode ?? mode) === 'login';

  return (
    <div>
      {!minimal && !lockMode ? (
        <div className="mb-4 rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          {t('auth.sign_in_customer_hint', {
            defaultValue: 'Sign in with the email and password for your customer account.',
          })}
        </div>
      ) : null}
      {!lockMode ? (
      <div className="mb-5 grid grid-cols-2 gap-0 border border-slate-200 text-xs font-semibold">
        <button
          type="button"
          onClick={() => {
            setMode('login');
            onModeChange?.('login');
            setError('');
            setSuccess('');
          }}
          className={`px-3 py-2.5 transition ${
            showLogin
              ? 'bg-slate-900 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {t('auth.tab_sign_in')}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('register');
            onModeChange?.('register');
            setError('');
            setSuccess('');
          }}
          className={`border-l border-slate-200 px-3 py-2.5 transition ${
            !showLogin
              ? 'bg-slate-900 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {t('auth.tab_register')}
        </button>
      </div>
      ) : null}

      {showLogin ? (
        <>
          {!minimal ? (
            <>
              <h2 className="mb-1 text-lg font-semibold tracking-tight sm:text-xl">
                {t('auth.sign_in_title')}
              </h2>
              <p className="mb-4 text-xs text-slate-500 sm:text-sm">
                {t('auth.sign_in_subtitle')}
              </p>
            </>
          ) : null}
          {auth.requiresEmailVerification && (
            <p className="mb-3 text-xs text-amber-600">
              {t('auth.verify_email_resent')}
            </p>
          )}
          {error && (
            <div className="mb-3 space-y-2">
              <p className={authErrorClass('light')}>{error}</p>
              {isDisabledAccountError ? (
                <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <span className="font-semibold">
                    {t('auth.need_help', { defaultValue: 'Need help?' })}
                  </span>{' '}
                  {t('auth.disabled_support_via', {
                    defaultValue: 'You can still contact support via',
                  })}{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/feedback')}
                    className="font-semibold text-slate-900 underline underline-offset-2"
                  >
                    {t('auth.send_feedback', { defaultValue: 'Send feedback' })}
                  </button>
                  .
                </div>
              ) : null}
            </div>
          )}
          {success ? <RegistrationSuccessBanner message={success} /> : null}
          <LoginForm
            onMfaRequired={() => {}}
            onError={(msg) => {
              setError(msg);
              setSuccess('');
            }}
          />
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 border-t border-slate-200 pt-4">
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className={authLinkClass('light')}
            >
              {t('auth.forgot_link', 'Forgot your password?')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/recover-account')}
              className={authLinkClass('light')}
            >
              {t('auth.use_recovery_code', { defaultValue: 'Use a recovery code' })}
            </button>
          </div>
          {!lockMode ? (
            <div className="mt-4 flex w-full justify-end">
              <button
                type="button"
                onClick={() => {
                  setMode('register');
                  onModeChange?.('register');
                  setError('');
                  setSuccess('');
                }}
                className={authSecondaryButtonClass('light')}
              >
                {t('auth.tab_register', 'Sign up')}
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <>
          {!minimal ? (
            <>
              <h2 className="mb-1 text-lg font-semibold tracking-tight sm:text-xl">
                {t('auth.register_title')}
              </h2>
              <p className="mb-4 text-xs text-slate-500 sm:text-sm">
                {t('auth.register_subtitle')}
              </p>
            </>
          ) : null}
          {error && <p className={`mb-2 ${authErrorClass('light')}`}>{error}</p>}
          {success ? <RegistrationSuccessBanner message={success} /> : null}
          <RegisterForm
            onRegistered={(email) => {
              setSuccess(
                `${t('auth.register_success_prefix')} ${email}. ${t(
                  'auth.register_success_suffix',
                )}`,
              );
              setError('');
              setMode('login');
            }}
            onError={(msg) => {
              setError(msg);
              setSuccess('');
            }}
            tone="customer"
          />
          <div className="mt-4 flex w-full justify-end">
            <button
              type="button"
              onClick={() => {
                setMode('login');
                onModeChange?.('login');
                setError('');
                setSuccess('');
              }}
              className={authSecondaryButtonClass('light')}
            >
              {t('auth.sign_in_instead', { defaultValue: 'Sign in instead' })}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default AuthPanel;
